import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { FieldPath } from 'react-hook-form';
import { z } from 'zod';
import { MarketCountryCode } from '@axo/shared/types';
import { LoanApplication, Product } from '@axo/shared/data-access/types';
import {
  SECURED_LOAN_AMOUNT_LIMITS,
  UNSECURED_LOAN_AMOUNT_LIMITS,
} from '../../../forms/createApplication/constants';

export function useLoanQuoteDataFormSchema(application: LoanApplication) {
  const amountLimitsConfig =
    application.Product === Product.SecuredLoan
      ? SECURED_LOAN_AMOUNT_LIMITS
      : UNSECURED_LOAN_AMOUNT_LIMITS;
  const quoteSchema = z.object({
    ID: z.string(),
    Amount: z.coerce
      .number({
        invalid_type_error:
          'Invalid number format. Use dots instead of commas.',
      })
      .min(
        amountLimitsConfig[application.MarketCountry as MarketCountryCode].min,
        { message: 'Market mininum amount required' },
      )
      .max(
        amountLimitsConfig[application.MarketCountry as MarketCountryCode].max,
        { message: 'Exceeds market amount limit' },
      )
      .nullable(),
    LenderMaxPossibleAmount: z.string().nullable(),
    Duration: z.coerce
      .number({
        invalid_type_error:
          'Invalid number format. Use dots instead of commas.',
      })
      .min(1)
      .max(99),
    NominalRate: z.coerce
      .number({
        invalid_type_error:
          'Invalid number format. Use dots instead of commas.',
      })
      .min(0)
      .max(100)
      .nullable(),
    EffectiveRate: z.coerce
      .number({
        invalid_type_error:
          'Invalid number format. Use dots instead of commas.',
      })
      .min(0)
      .max(100)
      .nullable(),
    AnnualRate: z.coerce
      .number({
        invalid_type_error:
          'Invalid number format. Use dots instead of commas.',
      })
      .min(0)
      .optional(),
    MonthlyPayment: z.coerce
      .number({
        invalid_type_error:
          'Invalid number format. Use dots instead of commas.',
      })
      .min(0)
      .nullable(),
    TotalPayment: z.coerce
      .number({
        invalid_type_error:
          'Invalid number format. Use dots instead of commas.',
      })
      .nullable(),
    StartupFee: z.coerce
      .number({
        invalid_type_error:
          'Invalid number format. Use dots instead of commas.',
      })
      .nullable(),
    AdministrationFee: z.coerce
      .number({
        invalid_type_error:
          'Invalid number format. Use dots instead of commas.',
      })
      .nullable(),
    RefinanceAmount: z.coerce
      .number({
        invalid_type_error:
          'Invalid number format. Use dots instead of commas.',
      })
      .nullable(),
    TopupAmount: z.coerce
      .number({
        invalid_type_error:
          'Invalid number format. Use dots instead of commas.',
      })
      .nullable(),
    DisbursedAt: z.string().date().nullable(),
    Status: z.string().nullable(),
    MarketCountry: z.string().nullable().optional(),
    LoanRequestID: z.string().nullable().optional(),
    LenderID: z.string().nullable().optional(),
    LenderReference: z.string().nullable().optional(),
    LoanApplicationID: z.string().nullable().optional(),
  });

  return z.record(z.string(), quoteSchema);
}

export function useLoanQuoteDataFormResolver(application: LoanApplication) {
  const schema = useLoanQuoteDataFormSchema(application);

  return useMemo(
    () => zodResolver(schema, { async: true }, { mode: 'async' }),
    [schema],
  );
}

export type LoanQuoteDataFormValues = z.infer<
  ReturnType<typeof useLoanQuoteDataFormSchema>
>;

export type LoanQuoteDataFormFieldPath = FieldPath<LoanQuoteDataFormValues>;
